import Button, { BUTTON_COLOR, BUTTON_VARIANT } from '@ab-core/button';
import type { FormObject } from '@ab-core/functions/formValidation';
import { ACTIONS, formReducer, getArrayFromFields } from '@ab-core/functions/formValidation';
import type { MappedProduct } from '@ab-core/graphql/dist';
import { Col } from '@ab-core/grid';
import Headline, { HEADLINE_SIZES } from '@ab-core/headline';
import { Icon } from '@ab-core/icon';
import Modal from '@ab-core/modal';
import { getTestIdProp } from '@ab-core/testing';
import type { FC } from 'react';
import React, { useCallback, useEffect, useReducer } from 'react';
import { SCALABLE_PRODUCT_CARD_TYPE, ScalableProductCard } from '../cardLayout';
import { useMappedProductToScalableProductSearchV2 } from '../mapper';
import type { ScalableProductCARD_STYLE_TYPE } from '../scalableStyles';
import { getScalableStyleFromLocalStorage, setScalableStyleToLocalStorage } from '../scalableStyles/functions';
import { defaultScalableStyle } from './../scalableStyles/presets/index';
import { ProductCardWrapper, SettingsWrapper } from './configurator.styled';
import {
    additionalInfos,
    availabilities,
    filterAdditionInfos,
    filterAvailabilities,
    filterPrices,
    prices
} from './objectTemplate';
import { Settings, SettingsMeta } from './settings';

type ConfiguratorType = { open?: boolean; onClose: () => void; product: MappedProduct };

export const ProductCardConfiguratorV2: FC<ConfiguratorType> = (props) => {
    const { open = false, onClose, product } = props;
    const formObject: FormObject = {
        fields: { ...availabilities, ...prices, ...additionalInfos },
        formIsValid: true
    };

    const [state, dispatch] = useReducer(formReducer, formObject);

    const selectOnChange = useCallback((key: string, value: boolean) => {
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key, props: { checked: value } }
        });
    }, []);
    const filteredAvailabilities = getArrayFromFields(state.fields).filter((f) => filterAvailabilities.includes(f.key));
    const filteredPrices = getArrayFromFields(state.fields).filter((f) => filterPrices.includes(f.key));
    const filteredAdditionInfos = getArrayFromFields(state.fields).filter((f) => filterAdditionInfos.includes(f.key));

    const userStyle: ScalableProductCARD_STYLE_TYPE = {
        ...defaultScalableStyle,
        ...{
            comparison: { isVisible: !!formObject.fields.comparison.inputProps.checked },
            positionText: { isVisible: !!formObject.fields.positionText.inputProps.checked },
            prices: {
                ...defaultScalableStyle.prices,
                showSurcharges: !!formObject.fields.showSurcharges.inputProps.checked,
                uvp: {
                    showUvpWithoutVat: !!formObject.fields.showUvpWithoutVat.inputProps.checked,
                    showUvpWithVat: !!formObject.fields.showUvpWithVat.inputProps.checked
                }
            },
            order: {
                ...defaultScalableStyle.order,
                showPackagingSize: !!formObject.fields.packagingSize.inputProps.checked
            },
            availabilities: {
                ...defaultScalableStyle.availabilities,
                showPickupChannels: !!formObject.fields.showPickupChannels.inputProps.checked
            },
            additionalLinks: { isVisible: !!formObject.fields.additionalLinks.inputProps.checked }
        }
    };

    useEffect(() => {
        const obj = getScalableStyleFromLocalStorage();
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key: 'positionText', props: { checked: obj.positionText?.isVisible } }
        });
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key: 'comparison', props: { checked: obj.comparison?.isVisible } }
        });
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key: 'additionalLinks', props: { checked: obj.additionalLinks?.isVisible } }
        });
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key: 'packagingSize', props: { checked: obj.order?.showPackagingSize } }
        });
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key: 'showPickupChannels', props: { checked: obj.availabilities?.showPickupChannels } }
        });
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key: 'showUvpWithoutVat', props: { checked: obj.prices?.uvp.showUvpWithoutVat } }
        });
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key: 'showUvpWithVat', props: { checked: obj.prices?.uvp.showUvpWithVat } }
        });
        dispatch({
            type: ACTIONS.SET_FIELD_PROPS,
            payload: { key: 'showSurcharges', props: { checked: obj.prices?.showSurcharges } }
        });
    }, []);

    const updateSettings = () => {
        setScalableStyleToLocalStorage(userStyle);
        onClose();
    };

    const settingsParts = [
        { fields: filteredAvailabilities, name: 'Verfügbarkeiten' },
        { fields: filteredPrices, name: 'Preise' },
        { fields: filteredAdditionInfos, name: 'Zusätzliche Informationen' }
    ];

    const convertedProduct = useMappedProductToScalableProductSearchV2([product]);

    return (
        <Modal
            tutorialId="product-configurator-modal"
            open={open}
            onClose={onClose}
            xl={5}
            headerText={
                <div className="flex gap-small2">
                    <Icon name="Filter" />
                    <Headline size={HEADLINE_SIZES.FOUR}>Produktkachel konfigurieren</Headline>
                </div>
            }
            footer={
                <div className="flex gap-small4">
                    <Button color={BUTTON_COLOR.GRAY} variant={BUTTON_VARIANT.OUTLINE} fluid onClick={() => onClose()}>
                        Abbrechen
                    </Button>
                    <Button
                        fluid
                        onClick={() => updateSettings()}
                        {...getTestIdProp('product-card-configurator-save-button')}
                    >
                        Speichern
                    </Button>
                </div>
            }
        >
            <div className="flex-justify-content-space-between ma-small3 ">
                <SettingsWrapper className="pa-small4">
                    {settingsParts.map((settings, index) => (
                        <Settings
                            key={index}
                            fields={settings.fields}
                            selectOnChange={selectOnChange}
                            name={settings.name}
                        />
                    ))}
                    <SettingsMeta />
                </SettingsWrapper>
                <ProductCardWrapper>
                    <Col>
                        {product &&
                            convertedProduct.map((productItem, key) => (
                                <ScalableProductCard
                                    key={key}
                                    product={productItem}
                                    cardType={SCALABLE_PRODUCT_CARD_TYPE.TILE}
                                    scalableStyle={userStyle}
                                />
                            ))}
                    </Col>
                </ProductCardWrapper>
            </div>
        </Modal>
    );
};
